import axios from 'axios';
import { Property, CreatePropertyDTO, UpdatePropertyDTO } from '../types/property.types';
import { UtilityService } from '../types/utility.types';
import { getAuthToken } from '../utils/auth';
import config from '../config/config';

// Use the centralized configuration for the API URL
const API_URL = config.apiUrl;

// Get headers with auth token
const getHeaders = () => {
  const token = getAuthToken();
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Convert server response to client type
export const convertToPropertyType = (data: any): Property => {
  if (!data) {
    return {
      _id: '',
      name: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        county: '',
        postcode: '',
        country: 'United Kingdom'
      },
      client: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      status: 'ACTIVE',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...data,
      utilityServices: []
    };
  }
  
  return {
    ...data,
    _id: data._id.toString(),
    client: data.client.toString(),
    contactName: data.contactName || '',
    contactEmail: data.contactEmail || '',
    contactPhone: data.contactPhone || '',
    utilityServices: data.utilityServices?.map((service: any) => ({
      ...service,
      _id: service._id.toString()
    })) || []
  }
};

// Get all properties for a client
export const getClientProperties = async (clientId: string): Promise<Property[]> => {
  try {
    
    const response = await axios.get(
      `${API_URL}/clients/${clientId}/properties`,
      getHeaders()
    );
    
    return response.data.map(convertToPropertyType);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Get property by ID
export const getProperty = async (clientId: string, propertyId: string): Promise<Property> => {
  try {
    
    const response = await axios.get(
      `${API_URL}/clients/${clientId}/properties/${propertyId}`,
      getHeaders()
    );
    
    return convertToPropertyType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Create new property
export const createProperty = async (clientId: string, propertyData: CreatePropertyDTO): Promise<Property> => {
  try {
    const response = await axios.post(
      `${API_URL}/properties`,
      { ...propertyData, clientId },
      getHeaders()
    );
    return convertToPropertyType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Update property
export const updateProperty = async (
  clientId: string,
  propertyId: string,
  propertyData: UpdatePropertyDTO
): Promise<Property> => {
  try {
    const response = await axios.put(
      `${API_URL}/clients/${clientId}/properties/${propertyId}`,
      propertyData,
      getHeaders()
    );
    return convertToPropertyType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Delete property
export const deleteProperty = async (clientId: string, propertyId: string): Promise<void> => {
  try {
    await axios.delete(
      `${API_URL}/clients/${clientId}/properties/${propertyId}`,
      getHeaders()
    );
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Add utility service
export const addUtilityService = async (
  clientId: string,
  propertyId: string,
  serviceData: Partial<UtilityService>
): Promise<Property> => {
  try {
    const response = await axios.post(
      `${API_URL}/clients/${clientId}/properties/${propertyId}/utility-services`,
      serviceData,
      getHeaders()
    );
    return convertToPropertyType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Update utility service
export const updateUtilityService = async (
  clientId: string,
  propertyId: string,
  serviceId: string,
  serviceData: Partial<UtilityService>
): Promise<Property> => {
  try {
    const response = await axios.put(
      `${API_URL}/clients/${clientId}/properties/${propertyId}/utility-services/${serviceId}`,
      serviceData,
      getHeaders()
    );
    return convertToPropertyType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Delete utility service
export const deleteUtilityService = async (
  clientId: string,
  propertyId: string,
  serviceId: string
): Promise<Property> => {
  try {
    const response = await axios.delete(
      `${API_URL}/clients/${clientId}/properties/${propertyId}/utility-services/${serviceId}`,
      getHeaders()
    );
    return convertToPropertyType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Error handling
const handleApiError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    return new Error(error.response?.data?.message || error.message);
  }
  return error as Error;
};
